import { DATA_SOURCES, ROOT_NAMES } from '@/config/constants';
import { apiDataSource, shapeEntity } from 'utils/entities';
import { hasSettled, retryTransient } from 'utils/api/fanOutQueries';
import bugsnagClient from 'containers/BugsnagClient';
import { fetchEntities } from 'utils/api/entities.api';
import { useQueries } from 'react-query';

/**
 * Ordered by how complete each taxonomy is, so the first source to answer wins
 * the name and image for a shared root.
 */
const ROOT_SOURCES = [
    DATA_SOURCES.POPULARITY,
    DATA_SOURCES.GOOGLE,
    DATA_SOURCES.TIKTOK,
    DATA_SOURCES.INSTAGRAM,
];

const fetchRootsFrom = async (dataSource) => {
    const entities = await fetchEntities({
        dataSource: apiDataSource(dataSource),
        entityType: 'categories',
        names: ROOT_NAMES,
    });
    return Object.values(entities || {})
        .map(shapeEntity)
        .filter(
            ({ depth, name }) => depth === 'cat1' && ROOT_NAMES.includes(name),
        );
};

// `onError` runs per mount and this hook is on every page, so report once.
const reported = new Set();
const reportUnresolved = (dataSource) => {
    if (reported.has(dataSource)) return;
    reported.add(dataSource);
    bugsnagClient.notify(
        new Error(`Industry roots unresolved for: ${dataSource}`),
        (event) => {
            event.severity = 'warning';
        },
    );
};

/**
 * Industry roots, resolved from every data source independently, so one
 * unhealthy database degrades that source's coverage instead of blanking the
 * home page and side nav. Each root carries the `sources` that resolved it.
 */
const useRootCategories = () => {
    const results = useQueries(
        ROOT_SOURCES.map((dataSource) => ({
            queryKey: ['category-roots', dataSource],
            queryFn: () => fetchRootsFrom(dataSource),
            staleTime: Infinity,
            cacheTime: Infinity,
            retry: retryTransient,
            onError: () => reportUnresolved(dataSource),
        })),
    );

    const byId = new Map();
    results.forEach(({ data }, index) => {
        (data || []).forEach((root) => {
            if (!byId.has(root.id)) byId.set(root.id, { ...root, sources: [] });
            byId.get(root.id).sources.push(ROOT_SOURCES[index]);
        });
    });

    // Hold the roots until every source has answered: a list that grows would
    // fire the industry data-points calls twice.
    const isLoading = !results.every(hasSettled);
    // Degrading is the point; resolving nothing is still a failure.
    const isError = !isLoading && !byId.size;

    return {
        data: isLoading || isError ? undefined : [...byId.values()],
        isLoading,
        isError,
        failedSources: ROOT_SOURCES.filter(
            (_, index) => results[index].isError,
        ),
        refetch: () => results.forEach(({ refetch }) => refetch()),
    };
};

export default useRootCategories;
